import type { JSX } from 'react';
import { deedsFor } from '@bratva/content';
import type { SaveDoc } from '@bratva/state';
import { canAct } from '@bratva/state';
import type { SceneTarget } from '../scene/Scene.js';
import { evaluate, perform } from '../game/deeds.js';
import { watchedNow } from '../game/heat.js';
import { store } from '../game/store.js';

interface Props {
  target: SceneTarget;
  save: SaveDoc;
  onClose(): void;
  onOpenPanel(which: 'shop' | 'garage'): void;
}

/** Что можно сделать здесь и сейчас. Открывается, когда пацан подошёл вплотную. */
export function DeedPanel({ target, save, onClose, onOpenPanel }: Props): JSX.Element | null {
  const deeds = deedsFor(target.kind, save.player.zone);
  if (deeds.length === 0) return null;

  const dayOver = !canAct(save);

  return (
    <div className="deeds">
      <div className="deeds-head">
        <b>{target.label ?? 'Тут'}</b>
        <button type="button" className="deeds-close" onClick={onClose} aria-label="Закрыть">
          ✕
        </button>
      </div>

      {dayOver && <p className="deeds-blocked">На сегодня всё: время вышло или нервы на пределе.</p>}
      {watchedNow(save) && <p className="deeds-blocked">Патруль рядом — палево за грязное дело втрое.</p>}

      <div className="deeds-list">
        {deeds.map((deed) => {
          const option = evaluate(deed, save, target.id);
          const disabled = !option.available || dayOver;
          return (
            <button
              key={deed.id}
              type="button"
              className="deed"
              disabled={disabled}
              onClick={() => {
                store.dispatch(perform(deed, save, target.id));
                // Магазин и гаражи — это панели, а не мгновенные действия:
                // время уже списано, дальше игрок выбирает сам.
                if (deed.type === 'shop') onOpenPanel('shop');
                if (deed.type === 'garage') onOpenPanel('garage');
              }}
            >
              <span className="deed-title">{deed.title}</span>
              <span className="deed-meta">
                {deed.minutes} мин
                {option.chance !== undefined && ` · ${Math.round(option.chance * 100)}%`}
                {deed.heat > 0 && ` · палево +${deed.heat}`}
              </span>
              <span className={option.available ? 'deed-note' : 'deed-note blocked'}>
                {option.reason ?? option.preview ?? deed.hint}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
